/**
 * A message's own cost (the LLM answer) combined with what reading it aloud
 * cost, for the shown price and its tooltip (ported from the MEIS-13490
 * prototype's `agentSpeechCost.ts`). The prototype's `cents`/`speechCostLine`
 * are not ported: they formatted with a hardcoded `toLocaleString`/German
 * label — the panel composes the display string itself via `useFormatting()`
 * and Tolgee (see `AgentChatBox.js`'s `MetaPart`), this module stays numbers
 * only.
 */

/** The cost shown under an answer: its own cost plus any speech cost added to it. */
export function totalCostUsd(
  answerCostUsd: number | null,
  speechCostUsd: number | null
): number | null {
  if (answerCostUsd === null && speechCostUsd === null) return null;
  return (answerCostUsd ?? 0) + (speechCostUsd ?? 0);
}

export type CostSplit = {
  totalUsd: number | null;
  modelUsd: number | null;
  speechUsd: number | null;
};

/**
 * Splits an answer's shown cost into its model share and its speech share.
 * Once this tab saw the backend book and store the speech share
 * (`storedSpeechCostUsd`, see `resolveStoredSpeechCostUsd`),
 * `answerCostUsd` already includes it: the model
 * share is the difference, and any live figure this tab tracked itself is
 * now redundant. Before that (a live, just-finished answer, whose meta part
 * never carries a stored share), the split is the answer's own cost plus
 * whatever this tab tracked while reading it aloud (`liveSpeechCostUsd`).
 * After a reload no share is known any more: the stored cost already holds
 * the speech cost and is shown as one total (design D16 keeps no separate
 * speech column).
 */
export function splitAnswerCost(
  answerCostUsd: number | null,
  storedSpeechCostUsd: number | null,
  liveSpeechCostUsd: number | null
): CostSplit {
  if (storedSpeechCostUsd !== null) {
    return {
      totalUsd: answerCostUsd,
      modelUsd:
        answerCostUsd !== null ? answerCostUsd - storedSpeechCostUsd : null,
      speechUsd: storedSpeechCostUsd,
    };
  }
  return {
    totalUsd: totalCostUsd(answerCostUsd, liveSpeechCostUsd),
    modelUsd: answerCostUsd,
    speechUsd: liveSpeechCostUsd,
  };
}

/**
 * Whether a message's own recorded live speech share (`AgentPanel`'s
 * `speechCostByMessageId`) is already folded into its cost override
 * (`AgentPanel`'s `costOverrideByMessageId`, kept in sync with
 * `chat.messages`), so `splitAnswerCost` should treat it as the *stored*
 * share instead of adding it again on top.
 *
 * `settledAnswerCostUsd` is the answer's cost as first observed once it
 * settled — `AgentChatBox`'s frozen `meta.costUsd`, or `AgentPanel`'s own
 * once-captured baseline for the header total — and never changes again.
 * The override starts out equal to that baseline (nothing reconciled yet:
 * a live speech cost tracked in this window is still genuinely outside the
 * answer's own cost, so `splitAnswerCost`'s fallback branch keeps adding it
 * on top). It only ever *moves* once a stored replay refreshes
 * `chat.messages` with a costUsd that already includes a previously booked
 * live share — at that point the recorded live share is that booked amount,
 * not a fresh one, and reusing it as `storedSpeechCostUsd` stops it from
 * being counted a second time. `undefined` (override not synced at all
 * yet) is treated the same as "not moved" — nothing to reconcile.
 */
export function resolveStoredSpeechCostUsd(
  settledAnswerCostUsd: number | null,
  costOverrideUsd: number | null | undefined,
  liveSpeechCostUsd: number | null
): number | null {
  if (
    costOverrideUsd === undefined ||
    costOverrideUsd === settledAnswerCostUsd
  ) {
    return null;
  }
  return liveSpeechCostUsd;
}
